/**
 * ConversationsPanel — the team's multi-turn agent runs, embeddable.
 *
 * Lives on the dashboard and the model requests tab (TRA-894): the places
 * people already look at traffic. Each row opens its transcript in a side
 * sheet (`ConversationDetailSheet`, TRA-1412); the open id lives in the
 * `conversation` URL param so a reload or a shared link reopens it. The
 * sheet links on to the conversation's full page.
 *
 * Every other control lives in the address bar too (TRA-1397), under the
 * same param names as the Requests view: `range`, `q`, `model` (team-wide
 * only), `tenant`, `environment`, `traffic`, `session`, `initiator`,
 * `sort` and `page`. The view toggle keeps the window, the search and the
 * filters, so the two views read the same traffic.
 *
 * Backed by the gateway's D1 conversation index, which knows each
 * conversation's models — `modelId` scopes the list to one platform model.
 */

import { useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ClientMetaCell } from '@/components/listing/ClientMetaCell'
import { CLIENT_META_COLUMNS } from '@/components/listing/clientMetadata'
import { ActiveFilters, FilterPopover } from '@/components/listing/filters'
import {
  activeFilterCount,
  metadataFilters,
  metadataQuery,
} from '@/components/listing/listingParams'
import { SortHeader } from '@/components/listing/SortHeader'
import { useListingParams } from '@/components/listing/useListingParams'
import { rowOpenProps } from '@/components/models/home/requestLogRow'
import {
  REQUEST_PAGE_SIZE,
  REQUEST_RANGE_OPTIONS,
  withSearchParam,
} from '@/components/models/home/requestLogOptions'
import {
  DEFAULT_REQUEST_RANGE,
  REQUEST_RANGES,
} from '@/components/models/home/requestLogParams'
import { ConversationDetailSheet } from '@/components/traces/ConversationDetailSheet'
import {
  CONVERSATION_SORT_FIELDS,
  DEFAULT_CONVERSATION_SORT,
  conversationMetadataFilters,
  conversationMetadataParams,
  conversationQuery,
} from '@/components/traces/conversationParams'
import { SideScrollArrows } from '@/components/trajectories/SideScrollArrows'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useConversations, type ConversationRange } from '@/hooks/useTraces'

function formatStamp(iso: string): string {
  const d = new Date(iso)
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const time = d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  return `${day} ${time}`
}

function formatDuration(fromIso: string, toIso: string): string {
  const seconds = Math.round(
    (new Date(toIso).getTime() - new Date(fromIso).getTime()) / 1000,
  )
  if (seconds < 60) return `${seconds.toString()}s`
  return `${Math.floor(seconds / 60).toString()}m ${(seconds % 60).toString()}s`
}

interface ConversationsPanelProps {
  /** Extra controls (e.g. a view toggle) rendered inline before the range
      select — keeps the toolbar on ONE line instead of stacking. */
  controls?: React.ReactNode
  /** Narrow to conversations that called this platform model. */
  modelId?: string
  /** The page this panel is on, for the full page's back arrow. */
  pageName: string
}

export function ConversationsPanel({
  controls,
  modelId,
  pageName,
}: ConversationsPanelProps): React.JSX.Element {
  const teamWide = modelId === undefined
  // Last week by default (TRA-905): "all time" asked the gateway to roll up
  // 1,000 conversations since epoch on every dashboard load, which at real
  // traffic volume held D1 long enough to time out the rest of the page.
  // All time is still one dropdown pick away for readers who want it.
  const listing = useListingParams({
    ranges: REQUEST_RANGES,
    defaultRange: DEFAULT_REQUEST_RANGE,
    sortFields: CONVERSATION_SORT_FIELDS,
    defaultSort: DEFAULT_CONVERSATION_SORT,
    metadataParams: conversationMetadataParams(teamWide),
  })
  const { searchParams, debouncedSearch, idSearch, effectiveRange, sort, page } = listing
  // The open row's id lives in the URL (TRA-1412), next to the request
  // sheet's `trace`, so closing it leaves every other param alone.
  const [, setSearchParams] = useSearchParams()
  const openConversationId = searchParams.get('conversation')

  function setOpenConversationId(next: string | null): void {
    setSearchParams((prev) => withSearchParam(prev, 'conversation', next), { replace: true })
  }

  const metadataSpecs = conversationMetadataFilters(teamWide)
  const filtered =
    debouncedSearch !== '' ||
    Object.keys(metadataQuery(searchParams, conversationMetadataParams(teamWide))).length > 0
  // Same page size as the request log (REQUEST_PAGE_SIZE): 20 keeps the
  // band skimmable, and the pager reaches the rest — the gateway paginates
  // in D1, so a page costs the same regardless of how many exist.
  const query = useConversations({
    params: conversationQuery({
      searchParams,
      range: effectiveRange,
      search: debouncedSearch,
      sort,
      page,
      modelId,
    }),
  })
  const rows = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / REQUEST_PAGE_SIZE))
  // Dropdown options are the distinct values on this page.
  const metadataOptions = metadataFilters(metadataSpecs, searchParams, rows)
  const activeCount = activeFilterCount(metadataOptions)
  const scrollRef = useRef<HTMLDivElement>(null)

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {controls}

          <Input
            value={listing.search}
            onChange={(event) => {
              listing.setSearch(event.target.value)
            }}
            placeholder="Search conversation, preview, tenant, session…"
            aria-label="Search conversations"
            className="h-8 w-[320px] font-mono text-xs"
          />

          {/* A pasted run id or trace key names one conversation of any age,
              so that search covers all time and the picker is held. */}
          <Select
            value={effectiveRange}
            disabled={idSearch}
            onValueChange={(v) => {
              listing.setRange(v as ConversationRange)
            }}
          >
            <SelectTrigger
              aria-label="Time range"
              title={idSearch ? 'An id search covers all time.' : undefined}
              className="h-8 w-auto min-w-[130px] gap-1 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REQUEST_RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <FilterPopover
            filters={metadataOptions}
            onMetadata={listing.setMetadata}
            emptyMessage="No filters apply to these conversations."
          />
        </div>
        {activeCount > 0 && (
          <ActiveFilters
            filters={metadataOptions}
            onMetadata={listing.setMetadata}
            onClear={listing.clearMetadata}
          />
        )}
      </div>

      {query.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : query.isError ? (
        <p className="text-sm text-destructive">
          Failed to load conversations. Refresh to retry.
        </p>
      ) : rows.length === 0 ? (
        <>
          <EmptyState
            title="No conversations"
            description={
              total > 0
                ? 'This page is past the end of the window. It moved while you were reading.'
                : filtered
                  ? 'No conversations match these filters.'
                  : 'Conversations appear once a client sends X-Conversation-Id. That header groups turns into one multi-turn run.'
            }
          />
          {/* Keep pagination when the WINDOW has rows but this page does not,
              or the reader is stranded on a dead page with no way back. */}
          {total > 0 ? (
            <Pagination
              currentPage={page}
              totalPages={pageCount}
              onPrevious={() => { listing.setPage(Math.max(1, page - 1)) }}
              onNext={() => { listing.setPage(Math.min(pageCount, page + 1)) }}
            />
          ) : null}
        </>
      ) : (
        <>
          {/* `relative` anchors the floating scroll arrows to this box. */}
          <div className="relative border border-border">
            <SideScrollArrows scrollRef={scrollRef} />
            <Table containerRef={scrollRef}>
              <TableHeader>
                <TableRow>
                  <TableHead>Conversation</TableHead>
                  <SortHeader field="run" label="Preview" sort={sort} onSort={listing.setSort} />
                  <SortHeader
                    field="turns"
                    label="Turns"
                    sort={sort}
                    onSort={listing.setSort}
                    alignRight
                  />
                  {CLIENT_META_COLUMNS.map((column) => (
                    <TableHead key={column.label} className="whitespace-nowrap">
                      {column.label}
                    </TableHead>
                  ))}
                  <TableHead className="whitespace-nowrap">Started</TableHead>
                  <SortHeader
                    field="last"
                    label="Last active"
                    sort={sort}
                    onSort={listing.setSort}
                  />
                  <TableHead className="text-right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.conversation_id}
                    {...rowOpenProps(row.conversation_id, setOpenConversationId, 'Open conversation')}
                    className="cursor-pointer hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <TableCell className="whitespace-nowrap font-mono text-xs text-foreground underline decoration-border underline-offset-2">
                      {row.conversation_id}
                    </TableCell>
                    <TableCell className="max-w-[320px] truncate text-xs text-muted-foreground">
                      {row.preview ?? '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {row.turn_count}
                    </TableCell>
                    {CLIENT_META_COLUMNS.map((column) => (
                      <ClientMetaCell key={column.label} value={column.value(row)} />
                    ))}
                    <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                      {formatStamp(row.first_seen)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-mono text-xs text-muted-foreground">
                      {formatStamp(row.last_seen)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right font-mono text-xs text-muted-foreground">
                      {formatDuration(row.first_seen, row.last_seen)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            currentPage={page}
            totalPages={pageCount}
            onPrevious={() => { listing.setPage(Math.max(1, page - 1)) }}
            onNext={() => { listing.setPage(Math.min(pageCount, page + 1)) }}
          />
        </>
      )}

      <ConversationDetailSheet
        conversationId={openConversationId}
        pageName={pageName}
        onClose={() => { setOpenConversationId(null) }}
      />
    </div>
  )
}
